/**
 * Подгон регуляторной надписи под долю площади окна.
 *
 * По ФЗ «О рекламе» предупреждение обязано занимать не меньше заданной доли
 * площади экрана. Прежняя реализация наращивала кегль циклом «+2px, пока
 * мало» и ничего не знала о ширине контейнера — из-за этого на 1440 строка
 * не влезала (1368px против 1364px) и переносилась на вторую, а кегль в CSS
 * приходилось подбирать руками (148px вместо макетных 150, отдельное
 * значение на 1920 и т. д.).
 *
 * Здесь размер считается формулой, а не перебором, и отдельно решается
 * задача «строка обязана остаться одной»: если натуральная ширина больше
 * доступной, текст сжимается по горизонтали (scaleX) и компенсируется по
 * вертикали (scaleY), чтобы площадь не потерялась.
 *
 * Три интерпретации «площади надписи» — регулятор её не уточняет:
 *   glyph — реально закрашенные пиксели букв (canvas getImageData);
 *   bbox  — сумма прямоугольных рамок букв (в 2–3 раза больше glyph);
 *   block — площадь всей плашки (проще всего защитить перед регулятором).
 *
 * Кегль из макета — НИЖНЯЯ граница, а не цель: закон требует «не меньше»,
 * поэтому если макетный кегль уже даёт нужную долю, он и остаётся.
 *
 * Совместимость: Safari 12+ (см. .browserslistrc). Canvas-замер идёт по
 * кэшу — на ресайзе без смены текста и шрифта getImageData не вызывается.
 */
itsoftApp.createModule('disclaimerFit', function () {
    const module = this;

    // Опорный кегль замера: считаем один раз при 100px и масштабируем.
    const REF_SIZE = 100;
    const LH_FALLBACK = 1.25;
    const ASCENT_FALLBACK = 0.8;
    const DESCENT_FALLBACK = 0.3;
    const BLOCK_MAX_ITER = 4;
    const BLOCK_TOLERANCE = 0.01;
    const AREA_MAX_ITER = 5;
    const AREA_TOLERANCE = 0.01;
    // Запас при обрезке бокса по чернилам, px (см. trimToInk).
    const INK_SAFETY = 1;
    // Ниже этой ширины контейнер считаем неизмеримым (скрыт / нет раскладки).
    const MIN_AVAIL_WIDTH = 8;

    let sharedCanvas = null;
    let sharedCtx = null;
    // WeakMap: элемент → { text, fontKey, mode, widthAtRef, areaAtRef }.
    let measureCache = new WeakMap();

    const getCtx = function () {
        if (sharedCtx === null && sharedCanvas) {
            return null;                    // уже пробовали, контекста нет
        }
        if (!sharedCanvas) {
            sharedCanvas = document.createElement('canvas');
            try {
                sharedCtx = sharedCanvas.getContext('2d', { willReadFrequently: true });
            } catch (err) {
                try {
                    sharedCtx = sharedCanvas.getContext('2d');
                } catch (err2) {
                    sharedCtx = null;
                }
            }
        }
        return sharedCtx;
    };

    /**
     * Ширина строки без canvas — временным span'ом в потоке документа.
     *
     * Нужна как страховка: 2d-контекста может не быть (canvas отключён
     * политикой приватности, урезанный WebView, серверный рендер в тестах).
     * Раньше модуль в этом случае падал на `ctx.font = …` («Cannot set
     * property 'font' of null»), и надпись оставалась с CSS-кеглем без
     * подгона — то есть регуляторное требование молча не выполнялось.
     */
    const measureWidthDom = function (text, style, sizePx) {
        const probe = document.createElement('span');
        probe.style.cssText = 'position:absolute;left:-9999px;top:0;white-space:pre;' +
            'visibility:hidden;pointer-events:none';
        probe.style.fontFamily = style.fontFamily || 'sans-serif';
        probe.style.fontWeight = style.fontWeight || '400';
        probe.style.fontStyle = style.fontStyle || 'normal';
        probe.style.letterSpacing = style.letterSpacing || 'normal';
        probe.style.textTransform = style.textTransform || 'none';
        probe.style.fontSize = sizePx + 'px';
        probe.textContent = text;
        document.body.appendChild(probe);
        const width = probe.getBoundingClientRect().width;
        document.body.removeChild(probe);
        return width;
    };

    const fontStringFrom = function (style, sizePx) {
        const weight = style.fontWeight || '400';
        const fStyle = style.fontStyle || 'normal';
        const family = style.fontFamily || 'sans-serif';
        return fStyle + ' ' + weight + ' ' + sizePx + 'px ' + family;
    };

    const resolveLineHeight = function (style) {
        const lh = parseFloat(style.lineHeight);
        const fs = parseFloat(style.fontSize);
        if (isFinite(lh) && isFinite(fs) && fs > 0) {
            return lh / fs;
        }
        return LH_FALLBACK;
    };

    /**
     * Площадь надписи при опорном кегле: 'bbox' — рамки букв,
     * 'glyph' — закрашенные пиксели.
     */
    const measureArea = function (text, fontString, mode) {
        const ctx = getCtx();
        ctx.font = fontString;

        const metrics = ctx.measureText(text);
        const ascent = metrics.actualBoundingBoxAscent || REF_SIZE * ASCENT_FALLBACK;
        const descent = metrics.actualBoundingBoxDescent || REF_SIZE * DESCENT_FALLBACK;
        const width = metrics.width;

        if (mode === 'bbox') {
            return { area: width * (ascent + descent), width: width };
        }

        const w = Math.max(1, Math.ceil(width) + 4);
        const h = Math.max(1, Math.ceil(ascent + descent) + 4);
        const canvas = ctx.canvas;
        canvas.width = w;
        canvas.height = h;
        // После смены размеров контекст сбрасывается — задаём заново.
        ctx.font = fontString;
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#000';
        ctx.fillText(text, 2, ascent + 2);

        const data = ctx.getImageData(0, 0, w, h).data;
        let filled = 0;
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] > 8) {
                filled += 1;
            }
        }
        return { area: filled, width: width };
    };

    /**
     * Обрезать бокс надписи по фактическим ЧЕРНИЛАМ.
     *
     * Строчный бокс шрифта всегда выше самих букв: под базовой линией
     * зарезервировано место под выносные элементы (у, р, д), а капс их
     * почти не использует. У Attentica при кегле 150px отведено 38px, а
     * реальных чернил под базовой линией — 12px. В итоге пустота снизу
     * получалась вдвое больше, чем сверху (39.6px против 20.3px), и плашка
     * занимала 20% высоты экрана вместо необходимого.
     *
     * Отрицательные margin'ы подтягивают кромки бокса к чернилам, и видимый
     * отступ становится равен padding'у контейнера — тому, что задан в CSS,
     * а не сумме padding + случайные метрики шрифта.
     *
     * Идемпотентно: положение чернил внутри бокса от margin'ов не зависит,
     * поэтому повторный вызов на ресайзе даёт тот же результат.
     */
    const trimToInk = function (textEl, innerEl, lines, style, size, sy) {
        const ctx = getCtx();
        if (!ctx) {
            return;
        }

        const fontStr = fontStringFrom(style, size);
        ctx.font = fontStr;
        const inkAsc = ctx.measureText(lines[0]).actualBoundingBoxAscent;
        ctx.font = fontStr;
        const inkDesc = ctx.measureText(lines[lines.length - 1]).actualBoundingBoxDescent;
        if (!isFinite(inkAsc) || !isFinite(inkDesc)) {
            return;                         // старый движок без ink-метрик
        }

        // Базовую линию ищем инлайн-блоком нулевой высоты: он садится на неё
        // нижней кромкой. Замер идёт ПОСЛЕ трансформа, поэтому scaleY уже
        // учтён в координатах, а расстояние до чернил масштабируем сами.
        const probe = document.createElement('span');
        probe.style.cssText = 'display:inline-block;width:0;height:0;overflow:hidden';
        innerEl.insertBefore(probe, innerEl.firstChild);
        const firstBaseline = probe.getBoundingClientRect().bottom;
        innerEl.appendChild(probe);
        const lastBaseline = probe.getBoundingClientRect().bottom;
        innerEl.removeChild(probe);

        const rect = textEl.getBoundingClientRect();
        // Пиксель запаса: подтягиваем ВПЛОТНУЮ к чернилам — и субпиксельное
        // округление вместе с `overflow: hidden` у плашки срезает ножки «Д».
        const topGap = (firstBaseline - inkAsc * sy) - rect.top - INK_SAFETY;
        const bottomGap = rect.bottom - (lastBaseline + inkDesc * sy) - INK_SAFETY;

        textEl.style.marginTop = (-Math.max(0, topGap)).toFixed(2) + 'px';
        textEl.style.marginBottom = (-Math.max(0, bottomGap)).toFixed(2) + 'px';
    };

    /**
     * Строки надписи. Перенос в макете авторский (<br>), поэтому ширину
     * считаем по САМОЙ ДЛИННОЙ строке, а площадь — суммой по всем: иначе
     * на 414px «лекарственным средством» не помещалось и браузер ломал
     * текст на три строки вместо макетных двух.
     */
    const splitLines = function (innerEl) {
        const lines = [];
        let current = '';

        // Идём по узлам, а не по innerHTML: перенос считается только если
        // <br> реально виден. В макете он есть на мобилке и скрыт на
        // десктопе (below-desktop-display) — по разметке строка одна там и
        // две тут, и подгон обязан считать по фактическому состоянию.
        const walk = function (node) {
            for (let i = 0; i < node.childNodes.length; i += 1) {
                const child = node.childNodes[i];
                if (child.nodeType === 1 && child.tagName === 'BR') {
                    if (window.getComputedStyle(child).display !== 'none') {
                        lines.push(current);
                        current = '';
                    }
                    continue;
                }
                if (child.nodeType === 1) {
                    if (window.getComputedStyle(child).display === 'none') {
                        continue;
                    }
                    walk(child);
                    continue;
                }
                if (child.nodeType === 3) {
                    current += child.nodeValue;
                }
            }
        };

        walk(innerEl);
        lines.push(current);

        // Canvas рисует текст как есть, а страница — с text-transform. Капс
        // из CSS при строчных буквах в разметке канвас мерил строчными: строка
        // на 15–20% уже нарисованной, площадь рамок меньше фактической
        // (cartilox, 19.10.2026: «БАД. Не является…» строчными в разметке).
        const transform = window.getComputedStyle(innerEl).textTransform;

        const clean = [];
        for (let i = 0; i < lines.length; i += 1) {
            let line = lines[i].replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
            if (transform === 'uppercase') {
                line = line.toUpperCase();
            } else if (transform === 'lowercase') {
                line = line.toLowerCase();
            }
            if (line) {
                clean.push(line);
            }
        }
        return clean.length ? clean : [''];
    };

    const getIntrinsics = function (el, lines, style, mode) {
        // Ключ кэша — строки ЧЕРЕЗ ПЕРЕНОС, а не через пробел. Разбивка на
        // строки зависит от ширины окна (авторский <br> виден только ниже
        // десктопа), а склеенный через пробел текст одинаков в обоих
        // случаях: после ресайза через брейкпойнт кэш отдавал замеры с
        // прежним числом строк, и подгон уходил в предел сжатия — на 1000
        // после 1440 scaleX 0.7 и одна строка вместо 0.92 и двух, на 375 —
        // одна строка вместо макетных двух (заказчик Elovera, 24.09.2026:
        // «при ресайзе размер дисклеймера должен обновляться»).
        const text = lines.join('\n');
        const fontKey = (style.fontWeight || '') + '|' +
            (style.fontStyle || '') + '|' + (style.fontFamily || '');
        const cached = measureCache.get(el);
        if (cached && cached.text === text && cached.fontKey === fontKey && cached.mode === mode) {
            return cached;
        }

        const fontStr = fontStringFrom(style, REF_SIZE);
        const ctx = getCtx();
        // Нет 2d-контекста — считаем плашку (block): она меряется геометрией
        // DOM и canvas не требует. Это ХУДШАЯ трактовка площади из трёх, но
        // рабочая; в DEBUG об этом сообщаем.
        const effectiveMode = ctx ? mode : 'block';

        if (!ctx && mode !== 'block') {
            DEBUG && console.warn('disclaimerFit: canvas недоступен, режим «' +
                mode + '» невозможен — считаем площадь плашки (block)');
        }

        if (ctx) {
            ctx.font = fontStr;
        }

        let widthAtRef = 0;
        let areaAtRef = 0;
        for (let i = 0; i < lines.length; i += 1) {
            if (ctx) {
                ctx.font = fontStr;
                widthAtRef = Math.max(widthAtRef, ctx.measureText(lines[i]).width);
            } else {
                widthAtRef = Math.max(widthAtRef, measureWidthDom(lines[i], style, REF_SIZE));
            }
            if (effectiveMode !== 'block') {
                const measured = measureArea(lines[i], fontStr, effectiveMode);
                areaAtRef += measured.area;
            }
        }

        if (effectiveMode !== 'block' && !areaAtRef) {
            return null;
        }

        const entry = {
            text: text,
            fontKey: fontKey,
            mode: effectiveMode,
            lineCount: lines.length,
            widthAtRef: widthAtRef,
            areaAtRef: effectiveMode === 'block' ? null : areaAtRef,
            // Замеры площади по фактическому кеглю: ключ — сам кегль.
            // Ширина шкалируется линейно и точно, площадь — нет (см. ниже).
            areaBySize: {}
        };
        measureCache.set(el, entry);
        return entry;
    };

    /**
     * Площадь букв при КОНКРЕТНОМ кегле.
     *
     * Экстраполяция с опорных 100px завышает площадь: доля пикселей,
     * закрашенных антиалиасингом по контуру, на мелком кегле больше, чем на
     * крупном. На надписи «не является лекарственным средством» это давало
     * 11% ошибки — расчёт считал, что 155px уже дают 5% площади, а по факту
     * выходило 4.4%, то есть НИЖЕ законного минимума. Поэтому итог
     * проверяем замером на том размере, который реально выставим.
     */
    const areaAtSize = function (entry, lines, style, mode, size) {
        // Ключ — ровно тот кегль, который уйдёт в CSS (там тоже toFixed(2)).
        // Округление крупнее давало замер «соседнего» размера и разброс
        // итоговой доли на ±0.1 п.п. вокруг порога.
        const key = size.toFixed(2);
        if (entry.areaBySize[key] !== undefined) {
            return entry.areaBySize[key];
        }
        const fontStr = fontStringFrom(style, size);
        let sum = 0;
        for (let i = 0; i < lines.length; i += 1) {
            sum += measureArea(lines[i], fontStr, mode).area;
        }
        entry.areaBySize[key] = sum;
        return sum;
    };

    /**
     * Уложить строку в доступную ширину: сжать по горизонтали и
     * компенсировать по вертикали. Возвращает итоговые кегль и масштабы.
     */
    const applyWidth = function (entry, size, ctx) {
        let sx = 1;
        let sy = 1;
        let s = size;

        if (!ctx.singleLine) {
            return { size: s, sx: sx, sy: sy, clamped: false };
        }

        const w1 = entry.widthAtRef / REF_SIZE;
        const natural = w1 * s;
        if (natural <= ctx.availW) {
            return { size: s, sx: sx, sy: sy, clamped: false };
        }

        const needed = ctx.availW / natural;
        if (needed >= ctx.sxMin) {
            sx = needed;
            // Сжатие по горизонтали компенсируем вертикалью, иначе площадь
            // надписи падает вместе с шириной.
            sy = Math.min(ctx.syMax, 1 / sx);
            return { size: s, sx: sx, sy: sy, clamped: false };
        }

        // Упёрлись в предел сжатия: кегль задаётся шириной, добрать площадь
        // можно только растяжением по вертикали.
        sx = ctx.sxMin;
        s = ctx.availW / (w1 * ctx.sxMin);
        sy = ctx.syMax;
        return { size: s, sx: sx, sy: sy, clamped: true };
    };

    /**
     * Подогнать надпись.
     *
     * @param {Element} textEl   элемент с текстом (кегль пишется на него)
     * @param {Element} innerEl  внутренняя обёртка (на неё идёт transform)
     * @param {Object}  options  ratio, mode, sxMin, syMax, singleLine, minSize
     * @returns {Object|null}    { size, scaleX, scaleY, coverage }
     */
    const fit = function (textEl, innerEl, options) {
        if (!textEl || !innerEl) {
            return null;
        }

        const opts = options || {};
        const ratio = opts.ratio > 0 ? opts.ratio : 0.05;
        const mode = opts.mode || 'bbox';
        const sxMin = opts.sxMin > 0 ? opts.sxMin : 0.7;
        const syMax = opts.syMax > 0 ? opts.syMax : 1.5;
        const singleLine = !!opts.singleLine;
        const minSize = opts.minSize > 0 ? opts.minSize : 0;

        // Перенос прошлого прогона снимаем: строку сначала пробуем уложить в
        // ОДНУ, и только если не выходит — разрешаем перенос (см. ниже).
        textEl.style.whiteSpace = '';

        const lines = splitLines(innerEl);
        if (!lines[0]) {
            return null;
        }

        const style = window.getComputedStyle(textEl);
        const lhBase = resolveLineHeight(style);
        const availW = textEl.parentElement
            ? textEl.parentElement.getBoundingClientRect().width
            : window.innerWidth;

        // Контейнер не измеряется (плашка скрыта на этом брейкпойнте, замер
        // до раскладки, безлэйаутная среда) — НЕ трогаем кегль: при availW→0
        // формула block-режима даёт абсурдный размер (ловили 17052px), а
        // «оставить значение макета» — всегда безопасный исход. Пересчёт
        // придёт со следующим resize или с document.fonts.ready.
        if (!(availW > MIN_AVAIL_WIDTH)) {
            DEBUG && console.warn('disclaimerFit: контейнер надписи не измеряется (' +
                availW + 'px) — оставляем кегль из CSS');
            return null;
        }

        const viewportArea = window.innerWidth * window.innerHeight;
        const targetArea = viewportArea * ratio;

        const intr = getIntrinsics(textEl, lines, style, mode);
        if (!intr) {
            return null;
        }

        // getIntrinsics мог понизить режим до 'block' (нет canvas).
        const usedMode = intr.mode;

        const wCtx = {
            availW: availW,
            singleLine: singleLine,
            sxMin: sxMin,
            syMax: syMax
        };

        let size;
        let sx = 1;
        let sy = 1;
        let wrapped = false;

        if (usedMode === 'block') {
            size = targetArea / (availW * lhBase * intr.lineCount);
            // Кегль из макета — нижняя граница: закон требует «не меньше».
            if (minSize && size < minSize) {
                size = minSize;
            }
            const placed = applyWidth(intr, size, wCtx);
            size = placed.size;
            sx = placed.sx;
            sy = placed.sy;
        } else {
            // Первое приближение — формулой от опорного замера, дальше
            // уточняем замером на фактическом кегле (см. areaAtSize).
            size = REF_SIZE * Math.sqrt(targetArea / intr.areaAtRef);
            if (minSize && size < minSize) {
                size = minSize;
            }

            let placed = applyWidth(intr, size, wCtx);
            for (let step = 0; step < AREA_MAX_ITER; step += 1) {
                const effective = areaAtSize(intr, lines, style, usedMode, placed.size) *
                    placed.sx * placed.sy;
                const err = (effective - targetArea) / targetArea;

                // Закон требует «НЕ МЕНЬШЕ» доли, поэтому допуск действует
                // только в плюс: 4.96% при пороге 5% — это нарушение, а не
                // «попали с точностью 1%».
                if (err >= 0 && err <= AREA_TOLERANCE) {
                    break;
                }
                // Не добираем, но кегль уже упёрся в ширину и предел
                // растяжения — расти дальше некуда.
                if (err < 0 && placed.clamped && placed.sy >= syMax - 1e-6) {
                    break;
                }
                // Перебор допустим, если кегль уже на макетном минимуме.
                if (err > 0 && minSize && placed.size <= minSize + 1e-6) {
                    break;
                }

                // Строка сжата, а вертикаль компенсирует не полностью (syMax
                // упёрся, при syMax 1 — не компенсирует вовсе): ширина
                // приколочена к контейнеру, и площадь растёт ЛИНЕЙНО от
                // кегля, а не квадратично. Шаг через корень тогда сходится
                // вдвое медленнее и за AREA_MAX_ITER оставлял недобор ~3%
                // (cartilox, 19.10.2026: надпись во всю ширину, как в макете).
                const linear = placed.sx < 1 && placed.sy < 1 / placed.sx - 1e-6;
                let next = placed.size * (linear
                    ? targetArea / effective
                    : Math.sqrt(targetArea / effective));
                // Шаг сошёлся, но снизу — подталкиваем вверх, иначе
                // остановимся на доли процента ниже нормы.
                if (err < 0 && next - placed.size < 0.05) {
                    next = placed.size + 0.5;
                }
                if (minSize && next < minSize) {
                    next = minSize;
                }
                if (err > 0 && placed.size - next < 0.05) {
                    break;
                }
                placed = applyWidth(intr, next, wCtx);
            }

            size = placed.size;
            sx = placed.sx;
            sy = placed.sy;
            // В упёршемся случае вертикаль тянем ровно настолько, насколько
            // не хватает площади, а не всегда до предела.
            if (placed.clamped) {
                const base = areaAtSize(intr, lines, style, usedMode, size) * sx;
                sy = Math.min(syMax, Math.max(1 / sx, targetArea / base));
            }

            // ── Фолбэк: строка НЕ влезает, а доля площади не набрана ──
            // Упёрлись в предел сжатия (sxMin) и растяжения (syMax), а
            // покрытие всё равно ниже требуемого. Закон требует «не меньше»,
            // поэтому лучше ПЕРЕНЕСТИ текст на следующую строку, чем оставить
            // недобор: с переносом ограничение по ширине снимается, и площадь
            // считается только от кегля (сумма чернил не зависит от того, как
            // строка разбита). Кегль решаем тем же итеративным замером.
            if (singleLine && placed.clamped) {
                const covered = areaAtSize(intr, lines, style, usedMode, size) * sx * sy;
                if (covered + 1e-6 < targetArea) {
                    wrapped = true;
                    sx = 1;
                    sy = 1;
                    size = REF_SIZE * Math.sqrt(targetArea / intr.areaAtRef);
                    if (minSize && size < minSize) {
                        size = minSize;
                    }
                    for (let step = 0; step < AREA_MAX_ITER; step += 1) {
                        const eff = areaAtSize(intr, lines, style, usedMode, size);
                        const err = (eff - targetArea) / targetArea;
                        if (err >= 0 && err <= AREA_TOLERANCE) {
                            break;
                        }
                        if (err > 0 && minSize && size <= minSize + 1e-6) {
                            break;
                        }
                        let next = size * Math.sqrt(targetArea / eff);
                        if (err < 0 && next - size < 0.05) {
                            next = size + 0.5;
                        }
                        if (minSize && next < minSize) {
                            next = minSize;
                        }
                        if (err > 0 && size - next < 0.05) {
                            break;
                        }
                        size = next;
                    }
                    DEBUG && console.warn('disclaimerFit: строка не влезает в одну — ' +
                        'разрешён перенос, кегль подобран по площади');
                }
            }
        }

        textEl.style.fontSize = size.toFixed(2) + 'px';
        innerEl.style.transform = 'scaleX(' + sx.toFixed(4) + ') scaleY(' + sy.toFixed(4) + ')';

        // Разрешаем перенос ИНЛАЙНОМ: CSS держит nowrap (разбивка строк —
        // авторская, из макета), и снимать его глобально нельзя.
        if (wrapped) {
            textEl.style.whiteSpace = 'normal';
        }

        // Сверка по DOM: ширина строки выше посчитана канвасом, а канвас
        // может мерить не тем шрифтом, что рисует страница (веб-шрифт ещё не
        // дошёл до канваса, другой движок, округления). Тогда строка шире
        // контейнера и уходит за край окна (Elovera, 24.09.2026: «на
        // 1025×779 дисклеймер выходит за правый край»). Мерим фактическую
        // ширину и дожимаем scaleX по факту; площадь добираем вертикалью.
        if (singleLine && !wrapped) {
            const drawnW = innerEl.getBoundingClientRect().width;
            if (drawnW > availW + 0.5) {
                sx *= availW / drawnW;
                sy = Math.min(syMax, Math.max(sy, 1 / sx));
                innerEl.style.transform = 'scaleX(' + sx.toFixed(4) + ') scaleY(' + sy.toFixed(4) + ')';
            }
        }

        // Высота бара обязана учитывать растяжение, иначе высокие буквы
        // обрежутся overflow'ом родителя.
        if (singleLine) {
            textEl.style.height = (size * sy * lhBase * intr.lineCount).toFixed(2) + 'px';
        } else {
            textEl.style.height = '';
        }

        // Подтянуть кромки бокса к буквам: иначе плашка несёт пустой
        // подстрочный запас шрифта и выглядит вдвое ниже снизу.
        if (usedMode !== 'block') {
            trimToInk(textEl, innerEl, lines, style, size, sy);
        }

        let coverage = 0;
        if (usedMode === 'block') {
            const rect = textEl.getBoundingClientRect();
            coverage = viewportArea ? (rect.width * rect.height) / viewportArea : 0;
        } else {
            // Замер на фактическом кегле, а не пересчёт с опорного, — иначе
            // отчёт покажет норму там, где её нет.
            const area = areaAtSize(intr, lines, style, usedMode, size) * sx * sy;
            coverage = viewportArea ? area / viewportArea : 0;
        }

        return { size: size, scaleX: sx, scaleY: sy, coverage: coverage, mode: usedMode, wrapped: wrapped };
    };

    /**
     * Итеративная доводка для режима block: padding и box-sizing родителя
     * сдвигают фактическую площадь относительно расчётной.
     */
    const fitBlock = function (textEl, innerEl, options) {
        let result = fit(textEl, innerEl, options);
        if (!result || result.mode !== 'block') {
            return result;
        }
        const target = (options.ratio || 0.05);
        for (let i = 0; i < BLOCK_MAX_ITER; i += 1) {
            const err = (result.coverage - target) / target;
            if (Math.abs(err) < BLOCK_TOLERANCE) {
                break;
            }
            const scaled = Object.assign({}, options, {
                minSize: result.size * Math.sqrt(target / result.coverage)
            });
            result = fit(textEl, innerEl, scaled);
            if (!result) {
                break;
            }
        }
        return result;
    };

    const invalidateCache = function () {
        measureCache = new WeakMap();
    };

    module.callback = {
        fit: fit,
        fitBlock: fitBlock,
        invalidateCache: invalidateCache
    };
});
